import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { TableConfig } from '../../../interfaces/table-config.interface';
import {
  StatementResponse,
  StatementRequest,
} from '../../../interfaces/gestioncobros.interfaces';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { GestionCobrosService } from '../../../services/gestioncobros.service';
import { AuthService } from '../../../services/auth.service';
import { exportarEstadoCuentas } from '../../../helpers/exportarEstadoCuentas';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

@Component({
  selector: 'app-estados',
  standalone: true,
  imports: [
    CommonModule,
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
  ],
  templateUrl: './estados.component.html',
  styleUrl: './estados.component.css',
})
export class EstadosComponent {
  insuranceData: StatementResponse[] = [];
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  errorStatus?: number;
  title: string = '';
  fecha1?: Date;
  fecha2?: Date;
  // Configuración de la tabla
  tableConfig: TableConfig = {
    columns: [
      {
        key: 'debtorCode',
        label: 'Codigo Cliente',
        sortable: true,
      },
      {
        key: 'clientName',
        label: 'Nombre del Asegurado',
        sortable: true,
      },
      {
        key: 'idNumber',
        label: 'Identificación',
        sortable: true,
      },
      {
        key: 'policyNumber',
        label: 'Póliza',
        sortable: true,
        type: 'poliza',
      },
      {
        key: 'lobDescription',
        label: 'Descripción del Ramo',
        sortable: true,
      },
      {
        key: 'sDate',
        label: 'Inicio',
        sortable: true,
        type: 'date', // Mantenemos fecha como texto
      },
      {
        key: 'wDate',
        label: 'Vencimiento',
        sortable: true,
        type: 'date', // Mantenemos fecha como texto
      },
      {
        key: 'transaction',
        label: 'Transacción',
        sortable: true,
      },
      {
        key: 'documentNumber',
        label: 'Número de Documento',
        sortable: true,
      },
      {
        key: 'amount',
        label: 'Monto',
        sortable: true,
        type: 'currency', // Para mostrar como 10,000.00 DOP
      },
    ],
    showTotals: true,
    totalFields: ['amount'],
  };


  opcionesSelectFilter = [
    { id: 'poliza', nombre: 'Póliza' },
    { id: 'codigo', nombre: 'Código' },
    { id: 'cedula', nombre: 'Cédula' },
    { id: 'rnc', nombre: 'RNC' },
    { id: 'nombre', nombre: 'Nombre' },
  ];

  // Referencia al componente de la tabla
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  constructor(
    private gestionCobrosService: GestionCobrosService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    this.title = 'Estados de Cuenta';
  }

  // Carga de datos iniciales
  loadInitialData(): void {
    const request: StatementRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      Date: new Date(),
    };

    this.gestionCobrosService.getStatements(request).subscribe({
      next: (response: HttpResponse<ApiResponse<StatementResponse[]>>) => {
        if (response.status === 200) {
          this.insuranceData = response.body?.data || [];
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al obtener las pólizas:', error);
        this.insuranceData = [];
        this.mensajeNoData = 'No se han podido obtener los datos';
      },
    });
  }

  // Filtro de datos
  onFilterChange(filters: any): void {
    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }

    this.fecha1 = filters.Date;
    console.log(filters)
    const request: StatementRequest = {
      producerNumber: this.authService.getProducerNumber() || 0,
      Date: filters.Date || new Date(),
       ...(filters?.policyNumber && { policyNumber: filters.policyNumber }),
      ...(filters?.clientCode && { clientCode: filters.clientCode }),
    };

    // Agregar campos opcionales solo si tienen valor

    if (filters.clientCode) request.clientCode = filters.clientCode;
    if (filters.paidInvoices) request.paidInvoices = filters.paidInvoices;

    this.gestionCobrosService.getStatements(request).subscribe({
      next: (response: HttpResponse<ApiResponse<StatementResponse[]>>) => {
        if (response.status === 200) {
          this.insuranceData = response.body?.data || [];
          this.errorStatus = undefined;
        } else if (response.status === 204) {
          this.insuranceData = [];
          this.mensajeNoData = 'No hay datos disponibles para mostrar';
        }
      },
      error: (error) => {
        console.error('Error al filtrar las pólizas:', error);
        this.insuranceData = [];
        this.mensajeNoData = 'No se han podido obtener los datos';
      },
    });
  }

  // Exportar datos
  onExportar(formato: string): void {
    //Evento para trackear las exportaciones de archivos en Google Analytics
     this.gAnalytics.trackEvent('estados_cuenta_exportar',{
       page:'/estados',
       format:formato
     })
    if (formato === 'pdf') {
      const params = {
        data: this.insuranceData,
        usuario: this.authService.getUsername()!,
        fecha: this.fecha1!,
      };
      exportarEstadoCuentas(params);
    } else {
      // Exportación de un solo cliente
      if (this.tablaComponent) {
        const headerInfo = {
          titulo: 'Estado de Cuentas',
          lob: this.getLobName(),
          slob: this.getSlobName(),
          fecha1: this.fecha1!,
          fecha2: this.fecha2,
          tableConfig: this.tableConfig,
        };
        this.tablaComponent.exportarDatos(formato, headerInfo);
      }
    }
  }

  // Extrayendo los nombres de los ramos y subramos
  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  private getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  private getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }
}
